import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, CreditCard, MinusCircle, RefreshCw, XCircle } from "lucide-react";
import { API_BASE_URL } from "@/lib/config";

interface StripeStatus {
  secretKey: "live" | "test" | "missing" | "invalid";
  publishableKey: "live" | "test" | "missing" | "invalid";
  webhookSecret: boolean;
  ready: boolean;
  plans: {
    plan: string; cycle?: "monthly" | "annual"; source: "admin" | "config" | "missing"; priceId: string | null; valid: boolean; productId?: string | null; problem?: string | null;
    /** What Stripe charges (cents) — read by the API for the admin to check the amount. */
    unitAmount?: number | null; currency?: string | null; interval?: string | null;
  }[];
}

const PLAN_LABEL: Record<string, string> = { pro: "Professional", enterprise: "Enterprise" };

function money(cents: number, currency?: string | null): string {
  const code = (currency || "brl").toUpperCase();
  try { return new Intl.NumberFormat("pt-BR", { style: "currency", currency: code }).format(cents / 100); } catch { return `${(cents / 100).toFixed(2)} ${code}`; }
}

function problemText(problem: string, cycleLabel: string): string {
  if (problem === "no_key") return "Sem chave secreta para consultar o produto";
  if (problem.startsWith("several_")) return `Este produto tem mais de um preço ${cycleLabel} ativo e nenhum é o padrão: cole em Editar plano o ID price_… do valor certo (ou arquive os outros no Stripe)`;
  if (problem.startsWith("no_")) return `Este produto não tem um preço ${cycleLabel} ativo no Stripe`;
  if (problem === "inactive_price") return "Este preço está arquivado no Stripe: o checkout recusa";
  if (problem === "wrong_interval") return `Este preço não é ${cycleLabel}`;
  return `Stripe recusou: ${problem.replace("stripe_error:", "")}`;
}

function Row({ label, ok, neutral, testId, children }: { label: string; ok: boolean; neutral?: boolean; testId?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5" data-testid={testId}>
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5 text-right text-sm">
        {ok ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : neutral ? <MinusCircle className="h-4 w-4 shrink-0 text-muted-foreground" /> : <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
        {children}
      </span>
    </div>
  );
}

/** System admin: whether "Fazer upgrade" can open a Stripe checkout, and what is missing when it cannot. */
export function StripeStatusCard() {
  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/stripe/status`, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
      if (res.ok) setStatus(await res.json());
    } catch { /* status stays empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = status;
  return (
    <Card className="mb-4" data-testid="card-stripe-status">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <CreditCard className="h-4 w-4" /> Pagamentos (Stripe)
              {s && <Badge variant={s.ready ? "default" : "destructive"}>{s.ready ? "Pronto" : "Incompleto"}</Badge>}
            </CardTitle>
            <CardDescription>
              O botão "Fazer upgrade" abre o checkout do Stripe. Precisa da chave secreta no servidor e do ID de preço (price_…) de cada plano,
              informado em Editar plano ou nas variáveis <code className="break-all">Stripe__ProMonthlyPriceId</code> / <code className="break-all">Stripe__EnterpriseMonthlyPriceId</code>.
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} disabled={loading} aria-label="Atualizar">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!s ? (
          <p className="text-sm text-muted-foreground">{loading ? "Carregando..." : "Não foi possível ler o estado do Stripe."}</p>
        ) : (
          <div className="divide-y rounded-lg border px-4 py-1">
            <Row label="Chave secreta (Stripe__SecretKey)" ok={s.secretKey === "live" || s.secretKey === "test"}>
              {s.secretKey === "live" ? "Chave de produção" : s.secretKey === "test" ? "Chave de teste (sk_test_…)" : s.secretKey === "invalid" ? "Chave inválida (placeholder?)" : "Não configurada"}
            </Row>
            {s.plans.map((p) => {
              const annual = p.cycle === "annual";
              const cycleLabel = annual ? "anual" : "mensal";
              return (
              <Row key={`${p.plan}-${p.cycle ?? "monthly"}`} label={`Preço do plano ${PLAN_LABEL[p.plan] ?? p.plan} (${cycleLabel})`} ok={p.valid} neutral={annual && p.source === "missing"} testId={`stripe-price-${p.plan}-${p.cycle ?? "monthly"}`}>
                {p.valid ? (
                  <span className="flex flex-col items-end">
                    <code className="text-xs">{p.priceId}</code>
                    {p.unitAmount != null && <span className="text-xs font-medium" data-testid={`stripe-amount-${p.plan}-${p.cycle ?? "monthly"}`}>{money(p.unitAmount, p.currency)} / {p.interval === "year" ? "ano" : "mês"}</span>}
                    {p.productId && <span className="text-xs text-muted-foreground">preço {cycleLabel} do produto {p.productId}</span>}
                  </span>
                ) : p.source === "missing" ? (
                  annual ? <span className="text-muted-foreground">Sem preço anual (opcional) — Editar plano → ID de preço anual; sem ele a opção “Anual” fica oculta</span> : "Sem ID de preço — Editar plano → ID de preço do Stripe"
                ) : p.problem && p.problem !== "placeholder" ? (
                  <span className="flex flex-col items-end text-destructive">
                    <span>{problemText(p.problem, cycleLabel)}</span>
                    {p.unitAmount != null && <span className="text-xs"><code>{p.priceId}</code> · {money(p.unitAmount, p.currency)} / {p.interval === "year" ? "ano" : "mês"}</span>}
                    <a href={p.productId ? `https://dashboard.stripe.com/products/${p.productId}` : `https://dashboard.stripe.com/prices/${p.priceId}`} target="_blank" rel="noopener noreferrer" className="text-xs underline">
                      {p.productId ? "abrir o produto no Stripe → Preços → copiar o ID price_…" : "abrir o preço no Stripe"}
                    </a>
                  </span>
                ) : (
                  <span className="flex flex-col items-end text-destructive">
                    <span>Valor inválido: <code className="text-xs">{p.priceId}</code></span>
                    <span className="text-xs">precisa ser <code>price_…</code> (ou o <code>prod_…</code> do produto, que o sistema converte)</span>
                  </span>
                )}
              </Row>
              );
            })}
            <Row label="Chave publicável (Stripe__PublishableKey)" ok={s.publishableKey === "live" || s.publishableKey === "test"}>
              {s.publishableKey === "live" ? "Chave de produção (pk_live_…)" : s.publishableKey === "test" ? "Chave de teste (pk_test_…)" : s.publishableKey === "invalid" ? "Chave inválida (placeholder?)" : "Não configurada: cadastro de fora do Brasil não consegue adicionar o cartão (e não envia documentos)"}
              {(s.publishableKey === "live") !== (s.secretKey === "live") && (s.publishableKey === "live" || s.secretKey === "live") ? " — modo diferente da chave secreta" : ""}
            </Row>
            <Row label="Webhook (Stripe__WebhookSecret)" ok={s.webhookSecret}>
              {s.webhookSecret ? "Configurado" : "Sem segredo: a ativação do plano acontece na volta do checkout (funciona), cancelamentos pelo Stripe não chegam"}
            </Row>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          Onde pegar o ID: <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer" className="underline">dashboard.stripe.com/products</a> → clique no produto do plano → seção <strong>Preços</strong> → clique no preço mensal → copie o <strong>ID</strong> (<code>price_…</code>).
          O ID do produto (<code>prod_…</code>) também serve: o sistema procura o preço mensal (e o anual, se existir) ativo dele; com mais de um ativo no mesmo ciclo, vale o <strong>preço padrão</strong> do produto (sem padrão, cole o <code>price_…</code>). Preço usado numa cobrança não muda de valor no Stripe: crie outro preço no produto, marque como padrão e arquive o antigo. Chave de teste gera checkouts de teste; a de produção (sk_live_) cobra de verdade.
          Plano anual: crie no mesmo produto um preço recorrente <strong>por ano</strong> e informe o valor anual (R$ e US$) em Editar plano; o app mostra o toggle Mensal/Anual só quando o valor anual está cadastrado.
        </p>
      </CardContent>
    </Card>
  );
}
